/**
 * Ring Size Guide - Web Component
 * Interactive ring measurement calculator with diameter / circumference
 * toggle (mm) and automatic size-chart row highlighting.
 *
 * Sizing strategy (Nouvel Heritage house preference):
 *   - ONE confident recommendation per measurement
 *   - Between two standard sizes, the LARGER size is primary
 *     (favors comfort over the knuckle, easier to size down later)
 *   - When the measurement sits within ~35% of the smaller size's
 *     boundary, a subtle "for a closer fit" alt note appears
 *   - Out-of-range values clamp to the nearest chart row
 */
import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/* Size chart data (2026-07 fix: keyed on French sizes)
   Each entry: [fr, us, diameterMm, circumferenceMm]
   Kept in ascending order by FR size, whole FR sizes 44-63 only.
   FR size = inner circumference in mm by definition. US equivalents
   for FR 48-59 match the variant option values on every ring PDP
   ("53 (FR) | 6.25 (US)") - the PDP data is the source of truth.
   FR 44-47 extend the same French trade chart. Diameter = FR / pi,
   rounded to 0.1 mm. Must stay in sync with the table rows in
   sections/ring-size-guide.liquid (row order drives highlightIdx). */
const SIZE_CHART = [
  [44, 3,    14.0, 44],
  [45, 3.25, 14.3, 45],
  [46, 3.75, 14.6, 46],
  [47, 4,    15.0, 47],
  [48, 4.75, 15.3, 48],
  [49, 5,    15.6, 49],
  [50, 5.25, 15.9, 50],
  [51, 5.75, 16.2, 51],
  [52, 6,    16.6, 52],
  [53, 6.25, 16.9, 53],
  [54, 6.75, 17.2, 54],
  [55, 7.25, 17.5, 55],
  [56, 7.5,  17.8, 56],
  [57, 8,    18.1, 57],
  [58, 8.25, 18.5, 58],
  [59, 8.75, 18.8, 59],
  [60, 9,    19.1, 60],
  [61, 9.5,  19.4, 61],
  [62, 10,   19.7, 62],
  [63, 10.25, 20.1, 63],
];

/* How close to the smaller size (as a fraction of the gap to the
   next larger size) before we show "for a closer fit" alt.
   0.35 = within the lower 35% of the gap -> user is close enough
   to the smaller size that it remains a reasonable choice. */
const CLOSER_FIT_THRESHOLD = 0.35;

class RingSizeGuide extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.mode = 'diameter';
  }

  /* Mode switching */
  setModeDiameter() {
    this._setMode('diameter');
  }

  setModeCircumference() {
    this._setMode('circumference');
  }

  _setMode(mode) {
    this.mode = mode;
    const isDiameter = mode === 'diameter';

    this.refs.modeDiameter.classList.toggle('rsg__mode-btn--active', isDiameter);
    this.refs.modeCircumference.classList.toggle('rsg__mode-btn--active', !isDiameter);
    this.refs.modeDiameter.setAttribute('aria-pressed', String(isDiameter));
    this.refs.modeCircumference.setAttribute('aria-pressed', String(!isDiameter));

    const input = this.refs.measureInput;
    input.placeholder = isDiameter
      ? input.dataset.placeholderDiameter || 'e.g. 17.3'
      : input.dataset.placeholderCircumference || 'e.g. 54';

    const label = this.refs.fieldLabel;
    if (label) {
      label.textContent = isDiameter
        ? label.dataset.labelDiameter || 'Inner diameter (mm)'
        : label.dataset.labelCircumference || 'Inner circumference (mm)';
    }

    /* Sensible min/max bumpers so the browser-level validation
       matches the chart bounds for the active mode. */
    if (isDiameter) {
      input.min = '13';
      input.max = '21';
    } else {
      input.min = '42';
      input.max = '65';
    }

    if (input.value) {
      this.calculateSize();
    }
  }

  /**
   * Locate the row(s) that bracket the raw measurement.
   * Returns { smaller, larger, smallerIdx, largerIdx, exact }
   *   - exact === true when raw matches a row value exactly
   *   - larger === null when raw is >= max chart value (clamp high)
   *   - smaller === null when raw is <= min chart value (clamp low)
   */
  _bracket(raw) {
    const col = this.mode === 'diameter' ? 2 : 3;

    /* Clamp low */
    if (raw <= SIZE_CHART[0][col]) {
      return { smaller: null, larger: SIZE_CHART[0], largerIdx: 0, exact: raw === SIZE_CHART[0][col] };
    }
    /* Clamp high */
    const lastIdx = SIZE_CHART.length - 1;
    if (raw >= SIZE_CHART[lastIdx][col]) {
      return { smaller: SIZE_CHART[lastIdx], larger: null, smallerIdx: lastIdx, exact: raw === SIZE_CHART[lastIdx][col] };
    }

    /* In range — find the pair it sits between */
    for (let i = 0; i < SIZE_CHART.length - 1; i++) {
      const lo = SIZE_CHART[i][col];
      const hi = SIZE_CHART[i + 1][col];
      if (raw === lo) {
        return { smaller: SIZE_CHART[i], larger: null, smallerIdx: i, exact: true };
      }
      if (raw > lo && raw < hi) {
        return {
          smaller: SIZE_CHART[i],
          larger: SIZE_CHART[i + 1],
          smallerIdx: i,
          largerIdx: i + 1,
          exact: false,
        };
      }
      if (raw === hi) {
        return { smaller: SIZE_CHART[i + 1], larger: null, smallerIdx: i + 1, exact: true };
      }
    }

    /* Defensive fallback — should not reach */
    return { smaller: SIZE_CHART[lastIdx], larger: null, smallerIdx: lastIdx, exact: false };
  }

  /**
   * Produce a recommendation.
   * Returns { primary, alt, highlightIdx }
   *   primary / alt are chart rows (or null for alt)
   *   highlightIdx is the index of the row to highlight in the table
   *
   * Logic:
   *   - Exact match or clamp: that row is primary, no alt.
   *   - Between two sizes: larger is primary (NH comfort preference).
   *     If measurement is within CLOSER_FIT_THRESHOLD of the smaller's
   *     boundary, surface the smaller as "for a closer fit".
   */
  _recommend(raw) {
    const bracket = this._bracket(raw);
    const col = this.mode === 'diameter' ? 2 : 3;

    /* Single-row outcome: exact match or clamp */
    if (bracket.larger === null || bracket.smaller === null) {
      const row = bracket.larger || bracket.smaller;
      const idx = bracket.largerIdx !== undefined ? bracket.largerIdx : bracket.smallerIdx;
      return { primary: row, alt: null, highlightIdx: idx };
    }

    /* Between two sizes: larger is primary */
    const lo = bracket.smaller[col];
    const hi = bracket.larger[col];
    const fraction = (raw - lo) / (hi - lo);

    const alt = fraction <= CLOSER_FIT_THRESHOLD ? bracket.smaller : null;

    return {
      primary: bracket.larger,
      alt,
      highlightIdx: bracket.largerIdx,
    };
  }

  /* Format a size value exactly as authored (quarter US sizes like
     5.25 and 5.75 must not be rounded — String() preserves them). */
  _formatSize(size) {
    return String(size);
  }

  /* Calculation entry point */
  calculateSize() {
    const raw = parseFloat(this.refs.measureInput.value);
    if (isNaN(raw) || raw <= 0) return;

    const { primary, alt, highlightIdx } = this._recommend(raw);

    /* Show the result area (use CSS class, not hidden attr, to preserve aria-live) */
    this.refs.resultArea.classList.remove('rsg__result--hidden');

    /* Clear previous table highlights */
    const rows = this.refs.sizeTable.querySelectorAll('tbody tr');
    rows.forEach((r) => r.classList.remove('rsg__table-highlight'));

    /* Highlight the matching table row */
    if (rows[highlightIdx]) {
      rows[highlightIdx].classList.add('rsg__table-highlight');
      // WCAG 2.3.3 / 2.2.2 — honor prefers-reduced-motion.
      rows[highlightIdx].scrollIntoView({
        behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        block: 'nearest',
      });
    }

    /* Update the result display — FR leads, matching the PDP pills */
    this.refs.primaryFr.textContent = `FR ${this._formatSize(primary[0])}`;
    this.refs.primaryUs.textContent = `US ${this._formatSize(primary[1])}`;

    /* Show or hide the "for a closer fit" alt note */
    if (alt !== null) {
      this.refs.altNote.hidden = false;
      this.refs.altSize.textContent = `FR ${this._formatSize(alt[0])} / US ${this._formatSize(alt[1])}`;
      this.refs.altFit.textContent = this.refs.altNote.dataset.closerText || 'for a closer fit';
    } else {
      this.refs.altNote.hidden = true;
    }

    /* Smooth-scroll to the result (respects prefers-reduced-motion) */
    this.refs.resultArea.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'nearest',
    });
  }
}

if (!customElements.get('ring-size-guide')) {
  customElements.define('ring-size-guide', RingSizeGuide);
}
